import React, { forwardRef, useContext, useEffect, useImperativeHandle, useRef, useState } from "react";
import classNames from "classnames";
import { Embeddable, EmbeddableImperativeAPI } from "./embeddable";
import { isQuestion,  getLinkedPluginEmbeddable, ActivityLayouts } from "../../utilities/activity-utils";
import { accessibilityClick } from "../../utilities/accessibility-helper";
import IconChevronRight from "../../assets/svg-icons/icon-chevron-right.svg";
import IconChevronLeft from "../../assets/svg-icons/icon-chevron-left.svg";
import { EmbeddableType, Page, SectionType } from "../../types";
import { Logger, LogEventName } from "../../lib/logger";
import { IGetInteractiveState, INavigationOptions } from "@concord-consortium/lara-interactive-api";
import useResizeObserver from "@react-hook/resize-observer";
import { nanoid } from "../../utilities/nanoid";
import { EmbeddableVisibilityContext } from "../embeddable-visibility-context";
import { getSectionColumns } from "../../utilities/section-columns";
import { useTabBanner } from "./disabled-questions-context";
import { DisabledQuestionsBanner } from "./disabled-questions-banner";

import "./section.scss";

interface IProps {
  page: Page;
  section: SectionType;
  activityLayout: number;
  questionNumberStart: number;
  teacherEditionMode?: boolean;
  setNavigation?: (refId: string, options: INavigationOptions) => void;
  pluginsLoaded: boolean;
  ref?: React.Ref<SectionImperativeAPI>;
  hiddenTab?: boolean;
  hideQuestionNumbers?: boolean;
  saveInteractiveStateHistory?: boolean;
  addRefToQuestionMap?: (refId: string, ref: any) => void;
}

export interface SectionImperativeAPI {
  requestInteractiveStates: (options?: IGetInteractiveState) => Promise<void>[];
}

const right = "right";
const left = "left";

export const Section: React.ForwardRefExoticComponent<IProps> = forwardRef((props, ref) => {
  const { activityLayout, page, section, questionNumberStart, hiddenTab, addRefToQuestionMap } = props;
  const [isSecondaryCollapsed, setIsSecondaryCollapsed] = useState(false);

  const visibility = useContext(EmbeddableVisibilityContext);
  const tabBanner = useTabBanner(section);
  // A notebook tab switch shows one section and hides another; both report it, which coalesces.
  const prevHiddenTab = useRef(hiddenTab);
  useEffect(() => {
    if (prevHiddenTab.current === hiddenTab) return;
    prevHiddenTab.current = hiddenTab;
    visibility?.queue("tabChange");
  }, [hiddenTab, visibility]);

  // Stable id wiring the collapsible "Hide/Show" trigger to the panel it controls,
  // referenced by the trigger's aria-controls for the disclosure ARIA relationship.
  const collapsiblePanelId = useRef(`secondary-column-panel-${nanoid()}`).current;

  const sectionDivRef = useRef<HTMLDivElement>(null);
  const primaryDivRef = useRef<HTMLDivElement>(null);
  const secondaryDivRef = useRef<HTMLDivElement>(null);
  const embeddableRefs = useRef<Record<string, React.RefObject<EmbeddableImperativeAPI>>>({});

  // cf. https://www.npmjs.com/package/@react-hook/resize-observer
  const useSize = (target: any) => {
    const [size, setSize] = React.useState();
    useResizeObserver(target, (entry: any) => setSize(entry.contentRect));
    return size;
  };
  // use this to get browser height when deiciding to pin or not
  const [screenHeight, getDimension] = useState({
    dynamicHeight: window.innerHeight
  });
  const setDimension = () => {
    getDimension({
      dynamicHeight: window.innerHeight
    });
  };
  useEffect(() => {
    window.addEventListener("resize", setDimension);
    return(() => {
      window.removeEventListener("resize", setDimension);
    });
  }, [screenHeight]);

  useImperativeHandle(ref, () => ({
    requestInteractiveStates: (options?: IGetInteractiveState) =>
      section.embeddables.map((embeddable: EmbeddableType) =>
        embeddableRefs.current[embeddable.ref_id]?.current?.requestInteractiveState(options) || Promise.resolve()
      )
  }));

  const renderEmbeddables = (embeddablesToRender: EmbeddableType[], questionNumStart: number, isSingleColumn?: boolean) => {
    let questionNumber = questionNumStart;
    return (
      <React.Fragment>
        { embeddablesToRender.map((embeddable, embeddableIndex) => {
            if (isQuestion(embeddable)) {
              questionNumber++;
            }
            const linkedPluginEmbeddable = getLinkedPluginEmbeddable(page, embeddable.ref_id);
            if (!embeddableRefs.current[embeddable.ref_id]) {
              embeddableRefs.current[embeddable.ref_id] = React.createRef<EmbeddableImperativeAPI>();
            }
            return (
              <Embeddable
                ref={embeddableRefs.current[embeddable.ref_id]}
                key={`embeddable-${embeddableIndex}-${embeddable.ref_id}`}
                embeddable={embeddable}
                sectionLayout={isSingleColumn ? "full-width" : section.layout}
                activityLayout={activityLayout}
                displayMode={section.secondary_column_display_mode}
                questionNumber={isQuestion(embeddable) ? questionNumber : undefined}
                linkedPluginEmbeddable={linkedPluginEmbeddable}
                teacherEditionMode={props.teacherEditionMode}
                setNavigation={props.setNavigation}
                pluginsLoaded={props.pluginsLoaded}
                hideQuestionNumbers={props.hideQuestionNumbers}
                saveInteractiveStateHistory={props.saveInteractiveStateHistory}
                addRefToQuestionMap={addRefToQuestionMap}
              />
            );
          })
        }
      </React.Fragment>
    );
  };

    const embeddableWrapperDivRef = React.useRef(null);
    const wrapperSize: any = useSize(embeddableWrapperDivRef);

    const renderPrimaryEmbeddables = (primaryEmbeddablesToRender: EmbeddableType[], questionNumStart: number) => {
    const maxAspectRatioEmbeddables = primaryEmbeddablesToRender.filter(e => {
      if (e.type === "ManagedInteractive") {
        return e.custom_aspect_ratio_method === "MAX";
      } else {
        return e.aspect_ratio_method === "MAX";
      }
    });

    const hasMaxAspectRatio = maxAspectRatioEmbeddables.length > 0;
    const containerClass = classNames("column", layout, "primary", {"expand": isSecondaryCollapsed},
                                      {"max-aspect-ratio": hasMaxAspectRatio});
    const isPinned = wrapperSize?.height < screenHeight.dynamicHeight;
    const wrapperClass = classNames ("embeddableWrapper", {"pinned": isPinned});
    return (
      <div className={containerClass} ref={primaryDivRef} data-cy="section-column-primary">
        <div className={wrapperClass} ref={embeddableWrapperDivRef}>
          {renderEmbeddables(primaryEmbeddablesToRender, questionNumStart)}
        </div>
      </div>
    );
  };

  const renderSecondaryEmbeddables = (secondaryEmbeddablesToRender: EmbeddableType[], questionNumStart: number) => {
    const collapsible = section.secondary_column_collapsible;
    const containerClass = classNames("column", layout, "secondary", {"collapsed": isSecondaryCollapsed});
    return (
      <div className={containerClass} ref={secondaryDivRef} data-cy="section-column-secondary">
        {secondaryEmbeddablesToRender.length > 0 && collapsible && renderCollapsibleHeader()}
        {/* The panel wrapper carries the id referenced by the trigger's aria-controls. It uses
            `display: contents` so the embeddables remain direct layout children of the column. */}
        <div id={collapsiblePanelId} className="collapsible-panel">
          {!isSecondaryCollapsed && renderEmbeddables(secondaryEmbeddablesToRender, questionNumStart)}
        </div>
      </div>
    );
  };

  const renderCollapsibleHeader = () => {
    const collapsibleColumnOnLeft = section.layout === "30-70" || section.layout === "40-60" || section.layout === "responsive-30-70" || section.layout === "responsive-50-50";
    const headerClass = `collapsible-header ${isSecondaryCollapsed ? "collapsed" : ""} ${collapsibleColumnOnLeft ? left : right}`;
    return (
      // A native <button> gives the disclosure its semantics and keyboard support (Enter/Space)
      // for free. aria-expanded reflects the open state; aria-controls points at the panel.
      <button type="button" className={headerClass} data-cy="collapsible-header"
            aria-expanded={!isSecondaryCollapsed} aria-controls={collapsiblePanelId}
            aria-label={isSecondaryCollapsed ? "Show content" : "Hide content"}
            onClick={handleCollapseHeader} >
        {isSecondaryCollapsed
          ? <React.Fragment>
              {renderCollapseArrow(collapsibleColumnOnLeft ? right : left)}
              <div>Show</div>
            </React.Fragment>
          : <React.Fragment>
              {!collapsibleColumnOnLeft && <div>Hide</div>}
              {renderCollapseArrow(collapsibleColumnOnLeft ? left : right)}
              {collapsibleColumnOnLeft && <div>Hide</div>}
            </React.Fragment>
        }
      </button>
    );
  };

  const renderCollapseArrow = (arrowType: "left" | "right" ) => {
    return (
      arrowType === left
        ? <IconChevronLeft
          width={32}
          height={32}
          fill={"white"}
          aria-hidden="true"
          focusable="false"
        />
        : <IconChevronRight
          width={32}
          height={32}
          fill={"white"}
          aria-hidden="true"
          focusable="false"
        />
    );
  };

  const handleCollapseHeader = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (accessibilityClick(e))  {
      Logger.log({
        event: LogEventName.toggle_collapsible_column,
        parameters:{ hide_column: !isSecondaryCollapsed }
      });
      setIsSecondaryCollapsed( !isSecondaryCollapsed );
      visibility?.queue("columnToggle");
    }
  };

  const layout = section.layout;
  const display_mode = section.secondary_column_display_mode;
  const singlePage = activityLayout === ActivityLayouts.SinglePage;
  const responsiveFullWidth = layout === "responsive-full-width";
  const responsive3070Column = layout === "responsive-30-70" || layout === "responsive-2-column";
  const responsive5050 = layout === "responsive-50-50";
  const responsiveSection = responsive3070Column || responsive5050 || responsiveFullWidth || layout === "responsive";
  const sectionClass = classNames("section",
                                  {"full-width": layout === "full-width" || singlePage},
                                  {"l_6040": layout === "60-40"},
                                  {"r_4060": layout === "40-60"},
                                  {"l_7030": layout === "70-30"},
                                  {"r_3070": layout === "30-70"},
                                  {"responsive": responsiveSection && !singlePage},
                                  {"stacked": display_mode === "stacked"},
                                  {"carousel": display_mode === "carousel"},
                                  {"hidden-tab": hiddenTab},
                                  {"tab-contents": activityLayout === ActivityLayouts.Notebook}
                                );
  const { stacked, singleColumn, left: leftColumnEmbeddables, right: rightColumnEmbeddables, leftIsPrimary: leftPrimary } =
    getSectionColumns(section, activityLayout);
  const responsiveDirection  = singleColumn ? "column" : "row";
  const responsiveDirectionStyle = { flexDirection: responsiveDirection } as React.CSSProperties;
  if (stacked) {
    return (
      <div className={sectionClass} ref={sectionDivRef} style={responsiveDirectionStyle} data-cy="section-single-column-layout">
        { tabBanner && <DisabledQuestionsBanner banner={tabBanner} tab /> }
        { renderEmbeddables(section.embeddables, questionNumberStart, singleColumn) }
      </div>
    );
  } else {
    const numQuestionsLeftColumn = leftColumnEmbeddables.filter(embeddable => isQuestion(embeddable)).length;
    const rightColumnQuestionNumberStart = questionNumberStart + numQuestionsLeftColumn;
    const columns = (
      <>
        {leftPrimary
          ? renderPrimaryEmbeddables(leftColumnEmbeddables, questionNumberStart)
          : renderSecondaryEmbeddables(leftColumnEmbeddables, questionNumberStart)
        }
        {leftPrimary
          ? renderSecondaryEmbeddables(rightColumnEmbeddables, rightColumnQuestionNumberStart)
          : renderPrimaryEmbeddables(rightColumnEmbeddables, rightColumnQuestionNumberStart)
        }
      </>
    );
    return (
      <div className={classNames(sectionClass, { "with-tab-banner": tabBanner })} ref={sectionDivRef} data-cy="section-split-layout">
        { tabBanner
          ? <>
              <DisabledQuestionsBanner banner={tabBanner} tab />
              <div className="section-columns">{columns}</div>
            </>
          : columns
        }
      </div>
    );
  }
});
Section.displayName = "Section";
